import { motion } from 'framer-motion';
import { Cpu, Shield, Eye, Workflow, GitMerge, Bell, FileCode, Layers } from 'lucide-react';

interface FeatureTier {
  title: string;
  tagline: string;
  color: string;
  bgGradient: string;
  features: {
    icon: React.ElementType;
    name: string;
    description: string;
  }[];
}

const tiers: FeatureTier[] = [
  {
    title: 'The Engine',
    tagline: 'Parallelize your thinking',
    color: 'primary',
    bgGradient: 'from-primary-500/10 to-primary-600/5',
    features: [
      {
        icon: GitMerge,
        name: 'Multi-Agent Orchestration',
        description: 'Spawn parallel agents that work simultaneously across different parts of your codebase.'
      },
      {
        icon: Workflow,
        name: 'Eager Execution',
        description: 'Tasks auto-execute when dependencies are met. No manual triggering required.'
      },
      {
        icon: Layers,
        name: 'Context Hierarchy',
        description: 'Task Memory → Stream Store → Shared Store. Context flows where it\'s needed.'
      }
    ]
  },
  {
    title: 'The Control Plane',
    tagline: 'Deterministic control over probabilistic agents',
    color: 'accent',
    bgGradient: 'from-accent-500/10 to-accent-600/5',
    features: [
      {
        icon: Bell,
        name: 'Attention Set',
        description: 'Human-in-the-loop queue. Critical decisions surface for approval, not buried in logs.'
      },
      {
        icon: FileCode,
        name: 'Executable Patterns',
        description: 'Reusable automation with loops, conditions, and polling. Beyond simple prompts.'
      },
      {
        icon: Eye,
        name: 'Glass Box Execution',
        description: 'See every step the agent takes. Full audit trail of decisions and actions.'
      }
    ]
  },
  {
    title: 'The Foundation',
    tagline: 'Trust through transparency',
    color: 'success',
    bgGradient: 'from-success-500/10 to-success-600/5',
    features: [
      {
        icon: Shield,
        name: 'Privacy Architecture',
        description: 'Local RAG, zero retention. Only relevant snippets sent to LLM, never your full codebase.'
      },
      {
        icon: Cpu,
        name: 'Multi-Model Support',
        description: 'Claude, GPT, Gemini, local models. Switch at runtime without changing your workflow.'
      },
      {
        icon: Workflow,
        name: 'MCP Integration',
        description: 'Extensible via Model Context Protocol. Connect to GitHub, Linear, Slack, and more.'
      }
    ]
  }
];

export function FeatureTiers() {
  return (
    <section className="py-24 bg-surface-800">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
            Built in Layers
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Power when you need it. Governance when you must. Trust throughout.
          </p>
        </motion.div>

        <div className="space-y-16">
          {tiers.map((tier, tierIndex) => (
            <motion.div
              key={tier.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: tierIndex * 0.1 }}
            >
              {/* Tier Header */}
              <div className="mb-8">
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-${tier.color}-500/10 border border-${tier.color}-500/20 text-${tier.color}-400 text-sm font-medium mb-3`}
                  style={{
                    backgroundColor: tier.color === 'primary' ? 'rgba(59, 130, 246, 0.1)' :
                      tier.color === 'accent' ? 'rgba(168, 85, 247, 0.1)' :
                      'rgba(34, 197, 94, 0.1)',
                    borderColor: tier.color === 'primary' ? 'rgba(59, 130, 246, 0.2)' :
                      tier.color === 'accent' ? 'rgba(168, 85, 247, 0.2)' :
                      'rgba(34, 197, 94, 0.2)',
                    color: tier.color === 'primary' ? '#60a5fa' :
                      tier.color === 'accent' ? '#c084fc' :
                      '#4ade80'
                  }}
                >
                  Tier {tierIndex + 1}
                </div>
                <h3 className="text-2xl font-bold text-text-primary mb-2">{tier.title}</h3>
                <p className="text-text-secondary italic">"{tier.tagline}"</p>
              </div>

              {/* Features Grid */}
              <div className="grid md:grid-cols-3 gap-6">
                {tier.features.map((feature, featureIndex) => (
                  <motion.div
                    key={feature.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: tierIndex * 0.1 + featureIndex * 0.05 }}
                    className="group"
                  >
                    <div className={`relative h-full bg-gradient-to-br ${tier.bgGradient} rounded-xl border border-surface-600 p-6 hover:border-surface-500 transition-colors`}>
                      <div className="mb-4 p-3 rounded-lg inline-block"
                        style={{
                          backgroundColor: tier.color === 'primary' ? 'rgba(59, 130, 246, 0.1)' :
                            tier.color === 'accent' ? 'rgba(168, 85, 247, 0.1)' :
                            'rgba(34, 197, 94, 0.1)'
                        }}
                      >
                        <feature.icon
                          className="w-6 h-6"
                          style={{
                            color: tier.color === 'primary' ? '#60a5fa' :
                              tier.color === 'accent' ? '#c084fc' :
                              '#4ade80'
                          }}
                        />
                      </div>
                      <h4 className="text-lg font-semibold text-text-primary mb-2">{feature.name}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
